/**
 * manager-cart.js
 * Gerencia operações de carrinho via Shopify Cart API.
 * Disponível como window.CartManager.
 *
 * ─── API pública ──────────────────────────────────────────────────────────
 *
 *   await window.CartManager.add(variantId, { quantity, properties, button, action })
 *   await window.CartManager.remove(lineItemKey, { button })
 *   await window.CartManager.update(lineItemKey, quantity, { button })
 *   await window.CartManager.updateAttributes(attributes, { button })
 *   await window.CartManager.applyDiscount(code, { button })`r`n *   await window.CartManager.removeDiscount({ button })
 *   await window.CartManager.get()
 *   await window.CartManager.clear()
 *
 *   window.CartManager.on('added', callback)    → retorna função de cleanup
 *   window.CartManager.off('added', callback)
 *
 * ─── Retorno dos métodos ──────────────────────────────────────────────────
 *
 *   { success: true,  data: {...} }
 *   { success: false, error: 'mensagem' }
 *
 * ─── Eventos DOM emitidos (CustomEvent em document) ───────────────────────
 *
 *   'cart:added'   → e.detail: { variantId, quantity, data, action }
 *   'cart:removed' → e.detail: { lineItemKey, data }
 *   'cart:updated' → e.detail: { lineItemKey?, quantity?, attributes?, data }
 *   'cart:cleared' → e.detail: { data }
 *   'cart:error'   → e.detail: { action, error, variantId?, lineItemKey? }
 *
 * ─── Eventos internos (via .on()) ─────────────────────────────────────────
 *
 *   'added', 'removed', 'updated', 'cleared', 'error'
 *   (mesmos dados do CustomEvent)
 */

/* ─── CartAPI ─────────────────────────────────────────────────────────────── */

class CartAPI {
  constructor(timeout = 5000) {
    this._timeout = timeout;
  }

  async _fetch(url, options = {}) {
    const controller = new AbortController();
    const timerId = setTimeout(() => controller.abort(), this._timeout);
    try {
      const res = await fetch(url, { ...options, signal: controller.signal });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || `HTTP ${res.status}`);
      }
      return res.json();
    } finally {
      clearTimeout(timerId);
    }
  }

  add(variantId, quantity = 1, properties = {}) {
    return this._fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity, properties }),
    });
  }

  update(lineItemKey, quantity) {
    return this._fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineItemKey, quantity }),
    });
  }

  remove(lineItemKey) {
    return this.update(lineItemKey, 0);
  }

  updateCart(payload = {}) {
    return this._fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  }

  get() {
    return this._fetch('/cart.js');
  }

  clear() {
    return this._fetch('/cart/clear.js', { method: 'POST' });
  }
}

/* ─── CartNotifier ────────────────────────────────────────────────────────── */

class CartNotifier {
  constructor() {
    this._listeners = {};
  }

  on(event, cb) {
    if (!this._listeners[event]) this._listeners[event] = [];
    this._listeners[event].push(cb);
    return () => this.off(event, cb);
  }

  off(event, cb) {
    if (!this._listeners[event]) return;
    this._listeners[event] = this._listeners[event].filter(fn => fn !== cb);
  }

  emit(event, data) {
    this._listeners[event]?.forEach(cb => cb(data));
    document.dispatchEvent(
      new CustomEvent(`cart:${event}`, { bubbles: true, detail: data })
    );
  }
}

/* ─── CartManager ─────────────────────────────────────────────────────────── */

class CartManager {
  constructor() {
    this._api      = new CartAPI();
    this._notifier = new CartNotifier();
  }

  on(event, cb)  { return this._notifier.on(event, cb); }
  off(event, cb) { this._notifier.off(event, cb); }

  _pushAddToCartEvent({ variantId, quantity, data, button }) {
    if (!['accepted', 'true'].includes(localStorage.getItem('theme-cookie-bar-accepted'))) return;

    const card = button?.closest?.('[data-product-card]');
    const plp = card?.closest?.('[data-plp-page-type]');
    const itemPrice = data?.price != null
      ? Number(data.price) / 100
      : Number(card?.dataset.analyticsItemPrice || 0);
    const item = {
      item_id: String(data?.product_id ?? card?.dataset.analyticsItemId ?? ''),
      item_name: data?.product_title ?? data?.title ?? card?.dataset.analyticsItemName,
      item_brand: card?.dataset.analyticsItemBrand || 'Charth',
      item_variant: String(data?.variant_id ?? variantId ?? card?.dataset.analyticsItemVariantId ?? ''),
      item_variant_name: data?.variant_title || undefined,
      price: itemPrice,
      quantity: Number(quantity) || 1,
      item_list_name: plp?.dataset.plpListName || undefined,
      item_url: card?.dataset.analyticsItemUrl || data?.url || undefined,
    };

    if (!item.item_id || !item.item_name) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'add_to_cart',
      event_id: `add-to-cart-${Date.now()}-${variantId}`,
      page_type: document.body.className.match(/template-([\w-]+)/)?.[1] || undefined,
      ecommerce: {
        currency: 'BRL',
        value: item.price * item.quantity,
        items: [item],
      },
    });
  }

  /**
   * Adiciona um item ao carrinho.
   * @param {string|number} variantId
   * @param {{ quantity?: number, properties?: object, button?: HTMLElement }} options
   */
  async add(variantId, { quantity = 1, properties = {}, button, action = 'open_drawer' } = {}) {
    if (button) window.ButtonFeedback?.loading(button);
    try {
      const data = await this._api.add(variantId, quantity, properties);
      if (button) window.ButtonFeedback?.success(button);
      this._notifier.emit('added', { variantId, quantity, data, action });
      this._pushAddToCartEvent({ variantId, quantity, data, button });
      return { success: true, data };
    } catch (error) {
      if (button) window.ButtonFeedback?.error(button);
      this._notifier.emit('error', { action: 'add', error: error.message, variantId });
      return { success: false, error: error.message };
    }
  }

  /**
   * Remove um item do carrinho.
   * @param {string} lineItemKey
   * @param {{ button?: HTMLElement }} options
   */
  async remove(lineItemKey, { button } = {}) {
    if (button) window.ButtonFeedback?.loading(button);
    try {
      const data = await this._api.remove(lineItemKey);
      if (button) window.ButtonFeedback?.success(button);
      this._notifier.emit('removed', { lineItemKey, data });
      return { success: true, data };
    } catch (error) {
      if (button) window.ButtonFeedback?.error(button);
      this._notifier.emit('error', { action: 'remove', error: error.message, lineItemKey });
      return { success: false, error: error.message };
    }
  }

  /**
   * Atualiza a quantidade de um item.
   * @param {string} lineItemKey
   * @param {number} quantity
   * @param {{ button?: HTMLElement }} options
   */
  async update(lineItemKey, quantity, { button } = {}) {
    if (button) window.ButtonFeedback?.loading(button);
    try {
      const data = await this._api.update(lineItemKey, quantity);
      if (button) window.ButtonFeedback?.success(button);
      this._notifier.emit('updated', { lineItemKey, quantity, data });
      return { success: true, data };
    } catch (error) {
      if (button) window.ButtonFeedback?.error(button);
      this._notifier.emit('error', { action: 'update', error: error.message, lineItemKey });
      return { success: false, error: error.message };
    }
  }

  async updateAttributes(attributes = {}, { button } = {}) {
    if (button) window.ButtonFeedback?.loading(button);
    try {
      const data = await this._api.updateCart({ attributes });
      if (button) window.ButtonFeedback?.success(button);
      this._notifier.emit('updated', { attributes, data });
      return { success: true, data };
    } catch (error) {
      if (button) window.ButtonFeedback?.error(button);
      this._notifier.emit('error', { action: 'updateAttributes', error: error.message, attributes });
      return { success: false, error: error.message };
    }
  }

  async applyDiscount(code, { button } = {}) {
    const trimmedCode = `${code || ''}`.trim();
    if (!trimmedCode) {
      return { success: false, error: 'Missing discount code' };
    }

    if (button) window.ButtonFeedback?.loading(button);
    try {
      const data = await this._api.updateCart({ discount: trimmedCode });
      const discountApplications = data.cart_level_discount_applications || [];
      const isApplied = discountApplications.length > 0;
      if (!isApplied) {
        await this._api.updateCart({ discount: '' });
        throw new Error('Código Inválido');
      }
      if (button) window.ButtonFeedback?.success(button);
      this._notifier.emit('updated', { discount: trimmedCode, data });
      return { success: true, data };
    } catch (error) {
      if (button) window.ButtonFeedback?.error(button);
      this._notifier.emit('error', { action: 'applyDiscount', error: error.message, discount: trimmedCode });
      return { success: false, error: error.message };
    }
  }

  async removeDiscount({ button } = {}) {
    if (button) window.ButtonFeedback?.loading(button);
    try {
      const data = await this._api.updateCart({ discount: '' });
      if (button) window.ButtonFeedback?.success(button);
      this._notifier.emit('updated', { discount: '', data });
      return { success: true, data };
    } catch (error) {
      if (button) window.ButtonFeedback?.error(button);
      this._notifier.emit('error', { action: 'removeDiscount', error: error.message });
      return { success: false, error: error.message };
    }
  }
  /** Retorna o estado atual do carrinho. */
  async get() {
    try {
      const data = await this._api.get();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /** Limpa o carrinho. */
  async clear() {
    try {
      const data = await this._api.clear();
      this._notifier.emit('cleared', { data });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

window.CartManager = new CartManager();
window.CartManager.on('added', refreshFreeShippingBar);
window.CartManager.on('removed', refreshFreeShippingBar);
window.CartManager.on('updated', refreshFreeShippingBar);
window.CartManager.on('cleared', refreshFreeShippingBar);

async function refreshFreeShippingBar() {
  const cart = await (await fetch('/cart.js')).json();

  const free_shipping_container = document.querySelector('.free-shipping-bar')
  const percentage_bar = document.querySelector('.free-shipping-bar__progress')

  const free_shipping_default = Number(free_shipping_container.getAttribute('data-free-shipping'))
  const subtotal = cart.items_subtotal_price / 100

  const result = free_shipping_default - subtotal 
  const percentage = (subtotal / free_shipping_default) * 100

  console.log(percentage)

  const message = document.querySelector('.free-shipping-bar__message')

  if(result > 0) {
    message.innerHTML = `Faltam <b> R$ ${result} </b> para você garantir FRETE GRÁTIS`
    percentage_bar.style.width = `${percentage}%`
  } else {
    message.innerHTML = "<b>Você ganhou FRETE GRÁTIS</b>"
    percentage_bar.style.width = "100%"
  }
}
